import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { LoadingIcon } from "@/components/ui/loading-icon";
import { cn } from "@/lib/utils";

/**
 * Shared Linear-minimal chrome for the onboarding + connect-agent surfaces:
 * a small, tight heading; quiet muted field labels; a "Skip" affordance that
 * only shows a contour on hover; and a neutral (NOT primary-tinted) forward
 * button. The green/primary accent is reserved for focused input borders only —
 * buttons stay neutral so the surface reads calm, like Linear.
 *
 * Text hierarchy is a strict THREE tiers, mirroring Linear (don't paint two
 * levels the same color):
 *   1. title       → `text-foreground`      (brightest; reused for input values
 *                                             + section-row titles)
 *   2. subtitle    → `text-foreground/70`   (a clearly-readable secondary — the
 *                                             headline hue, dimmed)
 *   3. field label → `text-muted-foreground`(the dim scaffolding tier)
 */

/** Compact step heading — small title + one secondary line. */
export function StepHeading({
  title,
  subtitle,
}: {
  title: string;
  subtitle?: string;
}) {
  return (
    <div className="space-y-1">
      <h1 className="text-xl font-semibold tracking-tight text-foreground">
        {title}
      </h1>
      {subtitle ? (
        <p className="text-sm text-foreground/70">{subtitle}</p>
      ) : null}
    </div>
  );
}

/** Quiet, small field label — the dimmest text tier (Linear-style scaffolding). */
export function StepFieldLabel({
  children,
  htmlFor,
}: {
  children: ReactNode;
  htmlFor?: string;
}) {
  return (
    <label
      htmlFor={htmlFor}
      className="block text-[13px] font-medium text-muted-foreground"
    >
      {children}
    </label>
  );
}

/** "Skip" — text only, a subtle contour appears on hover (no fill). */
export function SkipButton({
  onClick,
  disabled,
  children = "Skip",
}: {
  onClick: () => void;
  disabled?: boolean;
  children?: ReactNode;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="md"
      onClick={onClick}
      disabled={disabled}
      className="border border-transparent text-muted-foreground hover:border-border/60 hover:bg-transparent hover:text-foreground"
    >
      {children}
    </Button>
  );
}

/**
 * Right-aligned step actions: an optional Skip next to a neutral forward button.
 * The forward button uses the `secondary` (neutral dark/solid) variant — never
 * the primary-tinted default — so green stays off the buttons.
 */
export function StepActions({
  onSkip,
  skipLabel,
  primaryLabel,
  onPrimary,
  primaryDisabled,
  busy,
  busyLabel,
}: {
  onSkip?: () => void;
  skipLabel?: string;
  primaryLabel: string;
  onPrimary: () => void;
  primaryDisabled?: boolean;
  busy?: boolean;
  busyLabel?: string;
}) {
  return (
    <div className="flex items-center justify-end gap-1.5">
      {onSkip ? (
        <SkipButton onClick={onSkip} disabled={busy}>
          {skipLabel}
        </SkipButton>
      ) : null}
      <Button
        variant="secondary"
        size="md"
        onClick={onPrimary}
        disabled={primaryDisabled || busy}
        className={cn("min-w-[7rem]", busy && "gap-2")}
      >
        {busy ? (
          <>
            <LoadingIcon size={15} />
            {busyLabel ?? primaryLabel}
          </>
        ) : (
          primaryLabel
        )}
      </Button>
    </div>
  );
}
